// GeexStackedColumn — Geex frame 27 (workspace/design-refs/geex/
// 26-analytics-segmented-progress.png, "Customer Orders" — the paired
// frame ships in the same source PNG as GeexSegmentedBar/26 on the geex
// share grid): plain tonal bars per period, except the dominant
// (highest-value) column, which stacks ink-scale bands top-to-bottom with
// its value in a solid-ink chip — "one column tells the story, the rest
// are just context." Optional total/delta footer row.
//
// items: [{ label, value, dominant? }] — dominant defaults to the
// max-value item when no item explicitly flags one.
// total / totalLabel / delta — optional footer figures ("272" / "Total
// orders" / 2.11 → "+2.11%").
// ink: base foreground (default 'title'). onInk: text color for the
// dominant chip's solid-ink fill (default 'title-reversed' — the DS's
// pre-built inverse-of-title pair; on a fixed poster ink, pass the
// poster's own background hue/token explicitly since 'title-reversed'
// only inverts the ADAPTIVE pair). hue: optional — non-dominant bars
// shade the hue instead of ink-alpha. height: plot height — a
// spacing-scale token (default 'E1', ~144px at base scale).

export const GeexStackedColumn = {
  // FA204 — STACK_BANDS is used only by this component's descendant
  // handler (Bar.children below); carried via `scope` so frank
  // serialization keeps it (a bare module-scope const can silently read
  // as undefined once a component is frank.toJSON'd — el.scope.<name>
  // resolves from any descendant handler, same as MarketplaceKindRail's
  // `scope: { hydrate, cardComponent }`).
  scope: { STACK_BANDS: [1, 0.7, 0.45, 0.25] },
  flow: 'y',
  gap: 'C',
  items: [],
  ink: 'title',
  onInk: 'title-reversed',
  height: 'E1',

  Plot: {
    display: 'grid',
    gridTemplateColumns: (el) => `repeat(${(el.parent.items || []).length || 1}, 1fr)`,
    alignItems: 'end',
    gap: 'X',
    height: (el) => el.parent.height || 'E1',
    childrenAs: 'state',
    children: (el, s) => {
      const parent = el.parent
      const items = parent.items || []
      const max = items.reduce((m, it) => Math.max(m, Number(it.value) || 0), 0) || 1
      const hasExplicitDominant = items.some((it) => it.dominant)
      const ink = parent.ink || 'title'
      const onInk = parent.onInk || 'title-reversed'
      const hue = parent.hue
      return items.map((it, i) => {
        const value = Number(it.value) || 0
        const dominant = hasExplicitDominant ? !!it.dominant : value === max
        return {
          key: it.label || `col${i}`,
          pct: Math.max(value / max, 0.05) * 100,
          dominant,
          value: it.value,
          label: it.label || '',
          tone: hue ? `${hue}+20` : `${ink}.15`,
          ink,
          onInk
        }
      })
    },
    childExtends: {
      flow: 'y',
      gap: 'V',
      height: '100%',

      Col: {
        flow: 'y',
        justifyContent: 'flex-end',
        alignItems: 'center',
        flex: '1',
        gap: 'V',

        Chip: {
          padding: 'Y2 X2',
          round: 'radiusPill',
          background: (el, s) => (s.dominant ? s.ink : 'transparent'),
          Text: {
            tag: 'span',
            fontSize: 'X2',
            fontWeight: '700',
            fontFamily: 'Code',
            color: (el, s) => (s.dominant ? s.onInk : s.ink),
            text: (el, s) => String(s.value ?? '')
          }
        },

        Bar: {
          width: '100%',
          height: (el, s) => `${s.pct}%`,
          flow: 'y',
          overflow: 'hidden',
          background: (el, s) => (s.dominant ? 'transparent' : s.tone),
          childrenAs: 'state',
          children: (el, s) =>
            s.dominant
              ? el.scope.STACK_BANDS.map((op, i) => ({ key: `band${i}`, op, ink: s.ink }))
              : [],
          childExtends: {
            width: '100%',
            flex: '1',
            background: (el, s) => `${s.ink}.${Math.round(s.op * 90)}`
          }
        }
      },

      Label: {
        tag: 'span',
        fontSize: 'Z',
        textAlign: 'center',
        opacity: '0.45',
        color: (el, s) => s.ink,
        // `items[].label` is a KEY-IN-STATE carrier: a consumer may hand this
        // chart a bundle key (workspace/pages/admin/usage.js feeds
        // `admin.usage.attr.systemEmpty`). Resolved HERE, at the render site —
        // `translate()` returns its input unchanged when nothing resolves, so a
        // tenant-supplied label passes through untouched.
        text: (el, s) => el.call('polyglot', s.label || '')
      }
    }
  },

  Footer: {
    flow: 'x',
    align: 'baseline space-between',
    gap: 'B',
    hide: (el) => el.parent.total === undefined || el.parent.total === null,

    Left: {
      flow: 'y',
      gap: 'X',
      Total: {
        tag: 'span',
        fontSize: 'F',
        fontWeight: '700',
        fontFamily: 'Code',
        lineHeight: '0.95',
        color: (el) => el.parent.parent.ink || 'title',
        text: (el) => String(el.parent.parent.total ?? '')
      },
      TotalLabel: {
        tag: 'span',
        fontSize: 'Z',
        opacity: '0.45',
        color: (el) => el.parent.parent.ink || 'title',
        text: (el) => el.parent.parent.totalLabel || ''
      }
    },

    Delta: {
      tag: 'span',
      fontSize: 'Z',
      fontWeight: '700',
      fontFamily: 'Code',
      color: (el) => el.parent.ink || 'title',
      hide: (el) => el.parent.delta === undefined || el.parent.delta === null,
      text: (el) => {
        const n = Number(el.parent.delta) || 0
        return `${n > 0 ? '+' : ''}${n}%`
      }
    }
  }
}
